import { useMemo } from "react";

interface PaginationProps {
  page: number;
  pageCount: number;
  total?: number;
  /** Shown next to the page counter (e.g. "items" / "flights"). */
  itemLabel?: string;
  onChange: (page: number) => void;
}

/**
 * Shared pagination control. Renders Previous / page chips / Next + a total
 * item counter. Shows at most 7 page buttons (first, last, current ±2,
 * ellipses). Hidden entirely when there's only one page.
 */
export function Pagination({
  page,
  pageCount,
  total,
  itemLabel,
  onChange,
}: PaginationProps) {
  const visiblePages = useMemo(() => buildPageList(page, pageCount), [page, pageCount]);
  if (pageCount <= 1) {
    return (
      <div className="pagination pagination--simple">
        {total !== undefined && (
          <span className="pagination__total">
            {total.toLocaleString()} {itemLabel ?? "items"}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="pagination">
      <button
        type="button"
        className="pagination__arrow"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        ←
      </button>
      <div className="pagination__pages">
        {visiblePages.map((p, idx) =>
          p === "…" ? (
            <span key={`gap-${idx}`} className="pagination__gap">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`pagination__page${p === page ? " pagination__page--current" : ""}`}
              onClick={() => onChange(p)}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          ),
        )}
      </div>
      <button
        type="button"
        className="pagination__arrow"
        onClick={() => onChange(Math.min(pageCount, page + 1))}
        disabled={page >= pageCount}
        aria-label="Next page"
      >
        →
      </button>
      {total !== undefined && (
        <span className="pagination__total">
          {total.toLocaleString()} {itemLabel ?? "items"}
        </span>
      )}
    </div>
  );
}

function buildPageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return range(1, total);
  if (current <= 4) return [...range(1, 5), "…", total];
  if (current >= total - 3) return [1, "…", ...range(total - 4, total)];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}
